import { useEffect, useState } from 'react'
import { useSearch } from '@faststore/sdk'
import { getTopSearches } from 'src/utils/getTopSearches'
import DOMPurify from 'dompurify'
import { Link } from 'gatsby'
import { useMobile } from 'src/hooks/useMobile'
import { RecommendationProvider } from 'src/components/restructure/product/contexts/Recommendations/recommendations-context'
import GenericShelfv2 from 'src/components/restructure/product/shelves/GenericShelfV2'

interface TopSearch {
  term: string
}

type SearchNotFoundProps = {
  term?: string
}

function SearchNotFound({ term: searchTerm }: SearchNotFoundProps) {
  const [topSearches, setTopSearches] = useState<TopSearch[]>([])
  const { isMobile } = useMobile()
  const findTerm = useSearch()
  const getTerm =
    typeof DOMPurify.sanitize === 'function'
      ? DOMPurify.sanitize(findTerm?.state?.term ?? '')
      : findTerm?.state?.term ?? ''

  const imgDevice = isMobile ? 'mob' : 'desk'

  useEffect(() => {
    const handleGetTopSearches = async () => {
      const data = await getTopSearches()

      const searches = data?.searches

      if (!searches?.length) {
        return
      }

      setTopSearches(searches.slice(0, 8))
    }

    handleGetTopSearches()
  }, [])

  useEffect(() => {
    const obj = {
      event: 'search_error',
      page_full_url: window?.location?.href ?? '',
      page_title: 'Página de busca',
      page_path: window?.location?.pathname ?? '',
      full_referrer: document?.referrer ?? '',
      local_timestamp:
        new Date().toISOString().replace('T', ' ').replace('.', ' ') ?? '',
      local_timestamp_identifier:
        Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
      website_domain: window?.location?.host ?? '',
      referring_domain: document?.referrer ?? '',
      page_name: 'Página de busca',
      page_type: 'página de busca',
      page_language: 'pt-br',
      previous_page_type: 'NA',
      previous_page_name: 'NA',
      page_platform: 'vtex',
      page_number_viewed: 'NA',
      search_term: searchTerm ?? '',
      number_search_results: '0',
    }

    window.dataLayer = window?.dataLayer || []

    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push(obj)
  }, [searchTerm])

  return (
    <div className="font-inter max-w-[1280px] w-full mx-auto mb-28 restructure-small-desktop:mt-6 restructure-small-desktop:mb-20">
      <div className="flex flex-col mb-24 restructure-small-desktop:flex-row-reverse restructure-small-desktop:gap-[128px] restructure-small-desktop:mb-[6.5rem]">
        <img
          className="w-full max-w-[708px]"
          src={`https://decathlonstore.vteximg.com.br/arquivos/empty-search-${imgDevice}.jpg`}
          alt="Empty search result"
          data-test-id="no-results-image"
        />
        <div className="mt-8 px-md restructure-small-desktop:mt-0">
          <h1
            className="text-heading-sm text-[#101010] m-0 restructure-small-desktop:text-heading-lg max-w-[400px] break-words"
            data-test-id="no-results-title"
          >
            Não encontramos resultados para "{getTerm}".
          </h1>

          <p
            className="text-body-md text-[#383838] mt-5 mb-8 restructure-small-desktop:text-body-lg restructure-small-desktop:mt-6 restructure-small-desktop:mb-10"
            data-test-id="no-results-suggestions-text"
          >
            Você pode verificar a ortografia ou tentar termos de pesquisa menos
            específicos. Dá uma olhada nos termos mais buscados pelos
            esportistas:
          </p>

          <div
            className="flex flex-wrap gap-4"
            data-test-id="top-searches-container"
          >
            {topSearches.length
              ? topSearches.map(({ term }) => (
                  <div
                    className="flex flex-none justify-center items-center cursor-pointer py-xs px-sm bg-neutral02 text-body-sm text-center text-[#3E4751] border border-solid border-[#E1E4E7] rounded-full"
                    key={term}
                    data-test-id={`top-search-term-${term}`}
                  >
                    <Link to={`/pesquisa?q=${term}&sort=score_desc&page=0`}>
                      {term}
                    </Link>
                  </div>
                ))
              : null}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-24 px-md">
        <RecommendationProvider pageName="emptysearch" engines={['dy']}>
          <GenericShelfv2
            enableSession
            position="top"
            data-test-id="shelf-top"
            engine="dy"
          />
          <GenericShelfv2
            enableSession
            position="middle"
            data-test-id="shelf-middle"
            engine="dy"
          />
          <GenericShelfv2
            enableSession
            position="bottom"
            data-test-id="shelf-bottom"
            engine="dy"
          />
          <GenericShelfv2
            enableSession
            position="area bonus"
            data-test-id="shelf-area-bonus"
            engine="dy"
          />
        </RecommendationProvider>
      </div>
    </div>
  )
}

export default SearchNotFound
